import { useEffect, useRef, useState } from 'react';

/**
 * Countdown turn timer. Pure UI side; the engine accepts TIMEOUT as an action.
 *
 * - durationMs: e.g. 15000
 * - onTimeout: called when reaching 0
 * - active: timer pauses when false
 * - key: changing the key restarts the timer (e.g. when turn changes)
 */
export function useTurnTimer(opts: {
  durationMs: number;
  active: boolean;
  onTimeout: () => void;
  resetKey: string | number;
}) {
  const { durationMs, active, onTimeout, resetKey } = opts;
  const [remaining, setRemaining] = useState(durationMs);
  const startedAtRef = useRef<number>(Date.now());
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  // Reset on key change
  useEffect(() => {
    startedAtRef.current = Date.now();
    setRemaining(durationMs);
  }, [resetKey, durationMs]);

  useEffect(() => {
    if (!active) return;
    const tick = () => {
      const elapsed = Date.now() - startedAtRef.current;
      const left = Math.max(0, durationMs - elapsed);
      setRemaining(left);
      if (left <= 0) {
        onTimeoutRef.current();
      }
    };
    const id = window.setInterval(tick, 100);
    return () => window.clearInterval(id);
  }, [active, durationMs, resetKey]);

  return remaining;
}
